/**
 * Authentication utilities for cookie-based authentication
 * Auth tokens are in HTTP-only cookies, user data in regular cookie
 */

import type { User, UserRole } from "../types/shared.types";

const USER_COOKIE_NAME = "user_data";

interface CookieUser extends Pick<User, "id" | "username" | "email" | "role"> {
  first_name?: string;
  last_name?: string;
  preferred_language?: string;
}

interface ResourceWithOwner {
  owner_id?: number | string;
  owner_user_id?: number | string;
}

type ActionType = "edit" | "delete" | "view";

/**
 * Cookie utility functions
 */
const cookies = {
  /**
   * Set a cookie
   */
  set: (name: string, value: string, days: number = 7): void => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const sameSite = "Strict";
    const secure = window.location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=${sameSite}${secure}`;
  },

  /**
   * Get a cookie value
   */
  get: (name: string): string | null => {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(";");
    for (const element of ca) {
      let c = element;
      while (c.startsWith(" ")) c = c.substring(1);
      if (c.startsWith(nameEQ)) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  },

  /**
   * Delete a cookie
   */
  delete: (name: string): void => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  },
};

/**
 * Get current user from cookie
 */
export const getCurrentUser = (): CookieUser | null => {
  try {
    const userData = cookies.get(USER_COOKIE_NAME);
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

/**
 * Set current user in cookie
 */
export const setCurrentUser = (user: CookieUser | null): void => {
  if (user) {
    cookies.set(USER_COOKIE_NAME, JSON.stringify(user), 7); // 7 days
  } else {
    cookies.delete(USER_COOKIE_NAME);
  }
};

/**
 * Clear all auth data (user cookie)
 * Note: HTTP-only auth cookies are cleared by the server on logout
 */
export const clearAuthData = (): void => {
  cookies.delete(USER_COOKIE_NAME);
};

/**
 * Check if user is authenticated
 * Note: This checks cookie for user presence, but actual auth
 * validation is done via HTTP-only cookies on the server
 */
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  return !!user?.id;
};

/**
 * Check if current user has admin role
 */
const PRIVILEGED_ROLES: UserRole[] = [
  "application_admin",
  "organization_admin",
];

export const isAdmin = (): boolean => {
  const user = getCurrentUser();
  return !!user?.role && PRIVILEGED_ROLES.includes(user.role);
};

/**
 * Check if current user has one of the specified roles
 */
export const hasRole = (...roles: UserRole[]): boolean => {
  const user = getCurrentUser();
  return !!(user?.role && roles.includes(user.role));
};

/**
 * Check if user can perform an action on a resource
 */
export const canPerformAction = (
  resource: ResourceWithOwner,
  action: ActionType = "view",
): boolean => {
  const user = getCurrentUser();
  if (!user || !resource) return false;

  const ownerId = resource.owner_id ?? resource.owner_user_id;

  // Privileged roles can do everything
  if (PRIVILEGED_ROLES.includes(user.role as UserRole)) return true;

  // Department heads can edit/view but not delete others' items
  if (user.role === "head_of_recruitment" || user.role === "head_of_sales") {
    if (action === "delete") {
      return Number(user.id) === Number(ownerId);
    }
    return true;
  }

  // Regular users can only manage their own resources
  return Number(user.id) === Number(ownerId);
};

/**
 * Legacy function - maintained for backward compatibility
 * @deprecated Use getCurrentUser() instead
 */
export const ensureUserData = (): CookieUser | Record<string, never> => {
  return getCurrentUser() || {};
};

/**
 * Legacy function - maintained for backward compatibility
 * @deprecated Use canPerformAction() instead
 */
export const canDeleteIdea = (
  idea: ResourceWithOwner | null,
  user: CookieUser | null,
): boolean => {
  if (!idea || !user) return false;
  const userId = Number.parseInt(String(user.id));
  const ownerId = Number.parseInt(String(idea.owner_id));
  return userId === ownerId || PRIVILEGED_ROLES.includes(user.role as UserRole);
};
